import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle2 } from "lucide-react";
import muellerLogo from "@/assets/mueller-logo-new.png";

const ProductMueller = () => {
  return (
    <>
      <Helmet>
        <title>Mueller - Excellence en refroidissement du lait depuis 1940</title>
        <meta 
          name="description" 
          content="Mueller, leader mondial en refroidissement du lait, offre des solutions innovantes et durables pour préserver la qualité de votre production laitière." 
        />
      </Helmet>

      <div className="-mx-4 -mt-16">
        {/* Hero Section */}
        <section 
          className="relative bg-cover bg-center text-white py-32"
          style={{
            backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(https://images.unsplash.com/photo-1628187872324-24a48f8f7e9b?q=80&w=2070)',
            minHeight: '500px'
          }}
        >
          <div className="container mx-auto px-4">
            <div className="max-w-3xl">
              <div className="w-16 h-1 bg-accent mb-6"></div>
              <h1 className="text-3xl md:text-5xl font-bold mb-6">
                Mueller: Excellence en refroidissement du lait depuis 1940
              </h1>
              <p className="text-lg leading-relaxed mb-4">
                Depuis plus de 80 ans, Mueller se positionne comme un leader mondial dans le domaine du refroidissement du lait. Leur engagement envers l'innovation et la qualité a permis de développer des systèmes de refroidissement reconnus pour leur efficacité et leur durabilité.
              </p>
              <p className="text-lg leading-relaxed">
                Chez Agro, nous sommes fiers de représenter Mueller depuis de nombreuses années. Nous vous offrons une gamme complète de réservoirs de refroidissement du lait, conçus pour répondre aux besoins spécifiques de votre exploitation laitière, quelle que soit sa taille.
              </p>
            </div>
          </div>
        </section>

        {/* Logo Section */}
        <section className="bg-white py-16">
          <div className="container mx-auto px-4 text-center">
            <img 
              src={muellerLogo} 
              alt="Mueller Logo"
              className="mx-auto h-36 w-auto"
            />
          </div>
        </section>

        {/* Pourquoi choisir Mueller */}
        <section className="bg-gray-50 py-16">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Pourquoi choisir Mueller?
              </h2>
              <p className="text-lg text-muted-foreground">
                Les avantages d'un système de refroidissement de qualité supérieure
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Qualité du lait préservée */}
              <div className="bg-white p-8 rounded-lg shadow-sm">
                <div className="mb-4">
                  <CheckCircle2 className="w-12 h-12 text-accent" />
                </div>
                <h3 className="text-xl font-bold mb-4">Qualité du lait préservée</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Les systèmes de refroidissement Mueller maintiennent la température optimale du lait, garantissant ainsi sa fraîcheur et sa qualité. Cela se traduit par un lait de meilleure qualité et une valeur ajoutée pour votre exploitation.
                </p>
              </div>

              {/* Efficacité énergétique */}
              <div className="bg-white p-8 rounded-lg shadow-sm">
                <div className="mb-4">
                  <CheckCircle2 className="w-12 h-12 text-accent" />
                </div>
                <h3 className="text-xl font-bold mb-4">Efficacité énergétique</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Mueller met l'accent sur l'efficacité énergétique. Leurs systèmes de refroidissement sont conçus pour minimiser la consommation d'énergie, ce qui se traduit par des économies substantielles sur vos factures d'électricité.
                </p>
              </div>

              {/* Durabilité et fiabilité */}
              <div className="bg-white p-8 rounded-lg shadow-sm">
                <div className="mb-4">
                  <CheckCircle2 className="w-12 h-12 text-accent" />
                </div>
                <h3 className="text-xl font-bold mb-4">Durabilité et fiabilité</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Les réservoirs de refroidissement Mueller sont construits pour durer. Avec leur conception robuste et leurs matériaux de haute qualité, vous pouvez compter sur un fonctionnement fiable pendant de nombreuses années.
                </p>
              </div>

              {/* Facilité de nettoyage */}
              <div className="bg-white p-8 rounded-lg shadow-sm">
                <div className="mb-4">
                  <CheckCircle2 className="w-12 h-12 text-accent" />
                </div>
                <h3 className="text-xl font-bold mb-4">Facilité de nettoyage</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Mueller comprend l'importance de l'hygiène dans la production laitière. Leurs réservoirs sont conçus pour être faciles à nettoyer, ce qui garantit la propreté et réduit le risque de contamination.
                </p>
              </div>

              {/* Innovation constante */}
              <div className="bg-white p-8 rounded-lg shadow-sm">
                <div className="mb-4">
                  <CheckCircle2 className="w-12 h-12 text-accent" />
                </div>
                <h3 className="text-xl font-bold mb-4">Innovation constante</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Mueller est constamment à la recherche de nouvelles façons d'améliorer ses produits. En choisissant Mueller, vous bénéficiez des dernières innovations en matière de refroidissement du lait.
                </p>
              </div>

              {/* En savoir plus */}
              <div className="bg-accent/10 p-8 rounded-lg shadow-sm flex flex-col justify-center items-center text-center">
                <h3 className="text-xl font-bold mb-4">En savoir plus</h3>
                <p className="text-muted-foreground mb-6">
                  Pour en savoir plus, visitez le site web
                </p>
                <Button 
                  asChild 
                  variant="outline" 
                  className="border-accent text-accent hover:bg-accent hover:text-white"
                >
                  <a 
                    href="https://www.muellerinc.com/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    www.muellerinc.com
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>


        {/* CTA Final */}
        <section className="bg-[#2c2c2c] text-white py-16">
          <div className="container mx-auto px-4 text-center">
            <p className="text-sm uppercase tracking-wider mb-4">CONTACTEZ-NOUS</p>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Prêt à Optimiser Votre Production Laitière?
            </h2>
            <p className="text-base md:text-lg mb-2 max-w-4xl mx-auto leading-relaxed">
              Exploitez pleinement le potentiel de votre production laitière avec Agro. Contactez-nous dès aujourd&apos;hui pour découvrir comment
            </p>
            <p className="text-base md:text-lg mb-2 max-w-4xl mx-auto leading-relaxed">
              nos services et produits peuvent aider à améliorer l&apos;efficacité de votre exploitation et garantir la meilleure qualité de lait possible.
            </p>
            <p className="text-base md:text-lg mb-8 max-w-4xl mx-auto leading-relaxed">
              Nous sommes disponibles 24/7 pour vous assister dans vos urgences.
            </p>
            <Button asChild size="lg" className="bg-accent hover:bg-accent/90 text-white">
              <Link to="/contact">Contactez-nous dès maintenant!</Link>
            </Button>
          </div>
        </section>
      </div>
    </>
  );
};

export default ProductMueller;
